import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Cookie, Settings2, X } from 'lucide-react';
import {
  ALL_ACCEPTED,
  ONLY_NECESSARY,
  getConsent,
  setConsent,
  type ConsentChoice,
} from '@/lib/cookies';

type View = 'hidden' | 'banner' | 'settings';

export default function CookieBanner() {
  const [view, setView] = useState<View>('hidden');
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const existing = getConsent();
    if (!existing) {
      // показываем баннер чуть позже, чтобы не мешать первому впечатлению
      const t = setTimeout(() => setView('banner'), 600);
      return () => clearTimeout(t);
    }
    setAnalytics(existing.analytics);
    setMarketing(existing.marketing);
    return undefined;
  }, []);

  // Глобальный хук, открыть настройки извне (из футера, Privacy и т.п.)
  useEffect(() => {
    const open = () => {
      const existing = getConsent();
      if (existing) {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
      setView('settings');
    };
    window.addEventListener('resstro:open-cookie-settings', open);
    return () => window.removeEventListener('resstro:open-cookie-settings', open);
  }, []);

  const acceptAll = () => {
    setConsent(ALL_ACCEPTED);
    setAnalytics(true);
    setMarketing(true);
    setView('hidden');
  };

  const onlyNecessary = () => {
    setConsent(ONLY_NECESSARY);
    setAnalytics(false);
    setMarketing(false);
    setView('hidden');
  };

  const saveCustom = () => {
    const choice: ConsentChoice = { analytics, marketing };
    setConsent(choice);
    setView('hidden');
  };

  if (view === 'hidden') return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-x-0 bottom-0 z-[90] flex justify-center px-4 pb-4 sm:px-6 sm:pb-6"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
    >
      <div
        className="w-full max-w-3xl border border-line bg-white shadow-[0_24px_60px_-30px_rgba(14,23,71,0.35)]"
        style={{ backgroundColor: '#ffffff' }}
      >
        {view === 'banner' && (
          <div className="grid gap-5 p-5 sm:grid-cols-[1fr_auto] sm:items-center sm:p-6">
            <div className="flex items-start gap-4">
              <div
                aria-hidden="true"
                className="grid h-10 w-10 shrink-0 place-items-center border border-line text-orange"
              >
                <Cookie size={18} strokeWidth={1.6} />
              </div>
              <div>
                <div
                  id="cookie-banner-title"
                  className="text-[14px] font-medium leading-snug text-ink"
                >
                  Мы используем файлы cookies
                </div>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink/65">
                  Технические необходимы для работы сайта. Аналитические и маркетинговые
                  включаются только с вашего согласия. Подробнее в{' '}
                  <Link to="/legal/cookies" className="link">
                    Политике cookies
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:justify-end">
              <button
                type="button"
                onClick={() => setView('settings')}
                className="inline-flex items-center gap-1.5 border border-line bg-white px-4 py-2.5 text-[13px] font-medium text-ink/75 transition hover:border-ink hover:text-ink"
              >
                <Settings2 size={13} strokeWidth={1.75} />
                Настроить
              </button>
              <button
                type="button"
                onClick={onlyNecessary}
                className="border border-line bg-white px-4 py-2.5 text-[13px] font-medium text-ink/75 transition hover:border-ink hover:text-ink"
              >
                Только необходимые
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="bg-orange px-5 py-2.5 text-[13px] font-medium text-white transition hover:bg-[#d24a1c]"
              >
                Принять всё
              </button>
            </div>
          </div>
        )}

        {view === 'settings' && (
          <div className="p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                  Настройки cookies
                </div>
                <h3 className="mt-1 text-[18px] font-medium tracking-tight text-ink">
                  Выберите, какие данные мы можем собирать
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  // если согласия ещё нет, закрытие = только необходимые
                  if (!getConsent()) onlyNecessary();
                  else setView('hidden');
                }}
                aria-label="Закрыть"
                className="grid h-9 w-9 place-items-center text-ink/60 transition hover:text-ink"
              >
                <X size={16} strokeWidth={1.6} />
              </button>
            </div>

            <ul className="mt-5 divide-y divide-line border border-line">
              <CategoryRow
                title="Технические"
                description="Необходимы для работы сайта: сессия и защита от подделки запросов (CSRF). Эти данные собираются всегда."
                checked
                disabled
              />
              <CategoryRow
                title="Аналитические"
                description="Статистика посещений, конверсий и времени загрузки помогает понять, что улучшить. Включаются только с вашего согласия."
                checked={analytics}
                onChange={setAnalytics}
              />
              <CategoryRow
                title="Маркетинговые"
                description="Разрешают показывать релевантную рекламу и измерять её эффективность (Я.Метрика, пиксели соцсетей)."
                checked={marketing}
                onChange={setMarketing}
              />
            </ul>

            <p className="mt-5 text-[11.5px] leading-relaxed text-ink/55">
              Изменить выбор можно в любой момент: кнопка «Настроить cookies» в&nbsp;футере сайта.
              Подробности в&nbsp;
              <Link to="/legal/cookies" className="link">
                Политике cookies
              </Link>{' '}
              и&nbsp;
              <Link to="/legal/privacy" className="link">
                Политике конфиденциальности
              </Link>
              .
            </p>

            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={onlyNecessary}
                className="border border-line bg-white px-4 py-2.5 text-[13px] font-medium text-ink/75 transition hover:border-ink hover:text-ink"
              >
                Только необходимые
              </button>
              <button
                type="button"
                onClick={saveCustom}
                className="border border-line bg-white px-4 py-2.5 text-[13px] font-medium text-ink/75 transition hover:border-ink hover:text-ink"
              >
                Сохранить выбор
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="bg-orange px-5 py-2.5 text-[13px] font-medium text-white transition hover:bg-[#d24a1c]"
              >
                Принять всё
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function CategoryRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <li className="flex items-start justify-between gap-4 p-4 sm:p-5">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-medium text-ink">{title}</span>
          {disabled && (
            <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-ink/45">
              Всегда вкл.
            </span>
          )}
        </div>
        <p className="mt-1 text-[12px] leading-relaxed text-ink/60">{description}</p>
      </div>
      <Toggle
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        ariaLabel={`Категория ${title}`}
      />
    </li>
  );
}

function Toggle({
  checked,
  disabled,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange && onChange(!checked)}
      className={
        'relative inline-flex h-6 w-11 shrink-0 items-center transition-colors ' +
        (checked ? 'bg-orange' : 'bg-ink/20') +
        (disabled ? ' cursor-not-allowed opacity-70' : ' cursor-pointer')
      }
    >
      <span
        aria-hidden="true"
        className={
          'inline-block h-5 w-5 bg-white shadow transition-transform ' +
          (checked ? 'translate-x-[22px]' : 'translate-x-0.5')
        }
      />
    </button>
  );
}
